import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { BreakpointProvider, setDefaultBreakpoints } from 'react-socks';
import ErrorBoundary from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import Modal from 'react-modal';

// Pages
// import Checkout from 'Containers/Checkout';
import { AirCheckout } from 'Containers/AirCheckout';
import { HotelCheckout } from 'Containers/HotelCheckout';
import { CheckoutSuccess } from 'Containers/AirCheckout/success';

// Components
import Loader from '../Loader';
import Footer from '../Footer';
import NavBar from '../NavBar';
import Backgrounds from '../Backgrounds';
import PageNotFound from '../PageNotFound';
import HomeMultiCountry from 'Components/HomeMultiCountry';
import WhoWeAre from 'Components/WhoWeAre';
import BlogContainer from 'Features/blog/components/BlogContainer';
import ArticleContainer from 'Features/blog/components/ArticleContainer';
import PackageContainer from 'Features/packages/components/PackageContainer';
import PackageThankyou from 'Features/packages/components/PackageThankyou';
import FlightsHome from 'Features/flights/components/FlightsHome';
// import Proxi from 'Containers/Checkout/Proxi';
import HotelsHome from 'Features/hotels/components/HotelsHome';
import PackagesHome from 'Features/packages/components/PackagesHome';
import Login from 'Features/users/components/Login';
import Logout from 'Features/users/components/Logout';
import Reset from 'Features/users/components/Reset';
import QuotationsHome from 'Features/quotations/components/QuotationsHome';
import QuotationDetails from 'Features/quotations/components/QuotationDetails';
import TravelInsuranceHome from 'Features/travelInsurance/components/TravelInsuranceHome';
// import { CallMe } from 'Components/CallMe';

// History
import { history } from 'ReduxConfig/store';

// Constants
import { ogLanguage, routes } from 'Constants';
import { getAppConfiguration } from '../../actions';
import PrivateRoute from '../PrivateRoute';
import DestinationsHome from 'Features/destinations/components/DestinationsHome';
import LogoBackground from 'Assets/images/mv-logo-background.jpg';


import GatewayCancelTransaction from '../../../../Containers/GatewayCancelTransaction';
import { TiotomPromo } from 'Features/tiotomPromo';
import { ItauCallback } from 'Containers/Itau';

import { useKeepSessionLive } from 'Containers/Itau/useKeepSessionLive';
import { PaymentLink } from 'Containers/PaymentLink';
import ErrorPage from '../ErrorPage';
import BestDeals from 'Features/destinations/components/BestDeals';


Modal.setAppElement('#root');

const routing = routes;

// These breakpoints are deprecated and only used for react-socks
const breakpoints = [
  { xsmall: 0 },
  { small: 540 },
  { medium: 768 },
  { large: 960 },
  { xl: 1140 }
];

const selector = ({ appConfiguration: { loader, errorBoundaryKey } }) => ({
  loader,
  errorBoundaryKey
});

const App = () => {
  const { loader, errorBoundaryKey } = useSelector(selector, shallowEqual);
  const dispatch = useDispatch();

  useKeepSessionLive(60);

  useEffect(() => {
    dispatch(getAppConfiguration());
  }, []);

  setDefaultBreakpoints(breakpoints);

  return (
    <ConnectedRouter history={history}>
      <ErrorBoundary FallbackComponent={ErrorPage} key={errorBoundaryKey}>
        {loader && <Loader />}
        <Helmet>
          <meta charSet="utf-8" />
          <title>
            Tienda Naranja | Promociones en paquetes vacacionales, vuelos
            baratos internacionales, hoteles baratos y ofertas de viaje.
          </title>
          <meta
            name="description"
            content="¡Vuelos baratos internacionales, Hoteles baratos y ofertas, promociones en Paquetes vacacionales a los mejores destinos! Los mejores hoteles All Inclusive, Pagos en cuotas Fácil y Seguro online o por teléfono."
          />
          <meta
            name="keywords"
            content="hoteles, viajes on line, hoteles baratos, vuelos baratos internacionales, pasajes aereos, pasajes baratos, hospedaje, promociones en paquetes vacacionales, autos, seguro de viaje, cruceros, disney, alojamiento barato, viajes, promociones aereas, viajes en cuotas, excursiones"
          />
          <meta property="og:image" content={LogoBackground} />
          <meta property="og:image:width" content="400" />
          <meta property="og:site_name" content="mevuelo.com" />
          <meta property="og:title" content="mevuelo.com" />
          <meta
            property="og:description"
            content="¡Vuelos baratos internacionales, Hoteles baratos y ofertas, promociones en Paquetes vacacionales a los mejores destinos! Los mejores hoteles All Inclusive, Pagos en cuotas Fácil y Seguro online o por teléfono."
          />
          <meta property="og:type" content="website" />
          <meta property="og:locale" content={ogLanguage} />
        </Helmet>
        <Switch>
          <Route path={routes.notFound} component={PageNotFound} />
          <Route path={routes.errorFound} component={ErrorPage} />
          <BreakpointProvider>
            {process.env.REACT_APP_IS_GLOBAL === 'On' ? (
              <HomeMultiCountry />
            ) : (
              <>
                <Backgrounds />
                <NavBar />
                <Switch>
                  {/*HOME*/}

                  <Route
                    exact
                    path={routing.tiotomPromo}
                    component={TiotomPromo}></Route>
                  <Route
                    exact
                    path={routing.travelInsurance}
                    component={TravelInsuranceHome}></Route>
                  <Route
                    exact
                    path={routing.corporate}
                    component={() => {
                      window.location.href = 'https://corporativo.mevuelo.com';
                      return null;
                    }}></Route>
                  <Route exact path={routing.home} component={FlightsHome} />

                  {/*BLOG*/}
                  <Route exact path={routing.blog} component={BlogContainer} />
                  <Route
                    path={routing.blogCategory}
                    component={BlogContainer}
                  />
                  <Route
                    path={routing.blogArticle}
                    component={ArticleContainer}
                  />

                  {/*CHECKOUT*/}
                  <Route
                    exact
                    path={routing.checkoutCancelPayment}
                    component={GatewayCancelTransaction}
                  />
                  <Route
                    path={`${routing.airCheckoutSuccess}/:id`}
                    component={CheckoutSuccess}
                  />
                  {/* <Route
                    exact
                    path={routing.checkoutAtcIniPayment}
                    component={AtcInitPayment}
                  />
                  <Route
                    path={routing.checkoutAtcCheckPayment}
                    component={AtcCheckPayment}
                  /> */}
                  {/* <Route exact path={routing.thankYou} component={ThankYou} /> */}
                  <Route
                    exact
                    path={routing.airCheckout}
                    component={AirCheckout}
                  />
                  <Route
                    exact
                    path={routing.hotelCheckout}
                    component={HotelCheckout}
                  />
                  {/* <Route
                    exact
                    path={routing.processPayment}
                    component={ProcessPayment}
                  /> */}
                  {/* <Route path={routing.airCheckout} component={NewCheckout} />
                  CHECKOUT CORPO
                  <Route
                    exact
                    path={routing.thankYouCorpo}
                    component={ThankYouCorpo}
                  />
                  <Route
                    exact
                    path={routing.checkoutCorpo}
                    render={props => (
                      <ProxyCorpo>
                        <CheckoutCorpo {...props} />
                      </ProxyCorpo>
                    )}
                  /> */}

                  {/*WHO WE ARE*/}
                  <Route exact path={routing.whoWeAre} component={WhoWeAre} />

                  {/*MOTORES*/}
                  <Route
                    path={routing.packageItem}
                    component={PackageContainer}
                  />
                  <Route
                    exact
                    path={routing.packageThankyou}
                    component={PackageThankyou}
                  />
                  <Route path={routing.package} component={PackagesHome} />

                  <Route path={routing.flight} component={FlightsHome} />
                  {/* <Route
                    path={routing.flightMultiDestination}
                    component={MainSearch}
                  /> */}
                  <Route
                    exact
                    path={routing.flightSearch}
                    component={FlightsHome}
                  />
                  <Route
                    path={routing.callbackFromItau}
                    component={ItauCallback}
                  />

                  <Route path={routing.hotel} component={HotelsHome} />
                  <Route
                    exact
                    path={routing.hotelSearch}
                    component={HotelsHome}
                  />
                  <Route path={routing.login} component={Login} />
                  <Route path={routing.logout} component={Logout} />
                  <PrivateRoute
                    redirectTo="/"
                    path={routing.resetPassword}
                    component={Reset}
                  />

                  <PrivateRoute
                    redirectTo="/"
                    path={routing.quotations}
                    component={QuotationsHome}
                    exact
                  />

                  <PrivateRoute
                    redirectTo="/"
                    exact
                    path={routing.quotationsDetails}
                    component={QuotationDetails}
                  />

                  {/* AUTO SEARCH PER DESINATION*/}
                  <Route
                    exact
                    path={routing.destination}
                    component={BestDeals}
                  />

                  {/* PAYMENT LINK */}

                  <Route
                    exact
                    path={routing.paymentLink}
                    component={PaymentLink}
                  />

                  {/*NOT FOUND*/}

                  <Route component={PageNotFound} />
                </Switch>
                <Footer />
              </>
            )}
          </BreakpointProvider>
        </Switch>
        {/* <CallMe /> */}
      </ErrorBoundary>
    </ConnectedRouter>
  );
};

export default App;
